import React, { useEffect, useMemo, useState } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { AddCircle, ArrowBack, Close, Visibility } from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  INVOICE_TRACKING_HOSTNAME,
} from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { getOfflineData } from "../../../utils/offline-services";
import { isStoreRole } from "../../../utils/common-utils";
import FileUpload from "../../../../common-utilities/core/file-uploader";
import PRItemRow from "./add-item-modal";
import SingleStorePRSection from "./single-store-row";
import BudgetAvailability from "./budget-availability";
import ResolvedStoresViewer from "./resolved-stores-viewer";
import {
  PR_ITEM_MODEL,
  getPRTotalAmount,
  getLineItemTotal,
  getBudgetTypeLabel,
  getPooledQuantity,
  mapPrFileUploadResponse,
  normalizeCode,
  buildLocationFirstStores,
  filterStoreRoleLineItems,
  STORE_ROLE_BUDGET_CATEGORY_NAME,
} from "./helper";
import {
  downloadSingleStorePRTemplate,
  mapSingleStorePrUploadResponse,
} from "./single-store-upload";
import DownloadTemplateButton from "../../master-data/common-components/upload-template";
import { uploadTemplates } from "../../master-data/constants";
import { getFinancialYears } from "../../home/financial-year-dropdown";
import "./index.scss";

const renderHOColumnHeader = () => (
  <div className="pr-item-row pr-item-row-header">
    <span>Subcategory</span>
    <span>Item</span>
    <span>Description</span>
    <span>HSN</span>
    <span>UOM</span>
    <span>Qty</span>
    <span>Rate (₹)</span>
    <span>Tax %</span>
    <span>Amount</span>
    <span>Tax Amt</span>
    <span>Total</span>
    <span>Vendor</span>
    <span>Req. By Date</span>
    <span>Budget Head</span>
    <span>Remarks</span>
    <span></span>
  </div>
);

const CreatePR = () => {
  const navigate = useNavigate();
  const { budgetId: paramBudgetId } = useParams();
  const [searchParams] = useSearchParams();
  const siteId = searchParams.get("siteId");

  const userDetails = getOfflineData("user") || {};
  const { department: userDepartmentId } = userDetails;

  const prTypeFromUrl = searchParams.get("prType") || "";
  const prScopeFromUrl = searchParams.get("prScope") || "SINGLE_STORE";
  const lineItemIdsFromUrl = (searchParams.get("lineItemIds") || "").split(",").filter(Boolean);
  const siteIdFromUrl = searchParams.get("siteId") || "";

  const [activeBudgetId, setActiveBudgetId] = useState(paramBudgetId || null);
  const [prType, setPRType] = useState(prTypeFromUrl);
  const [prScope, setPRScope] = useState(prScopeFromUrl);
  const isHeadOffice = prScope === "HEAD_OFFICE";

  // The HO site actually selected in the launcher modal (or, when editing, the PR's own
  // saved `site`) — distinct from `budgetDetails.site`, which for HEAD_OFFICE PRs is just
  // one arbitrary pooled STORE whose budget was scraped for the category line-item list.
  const [hoContextSiteId, setHoContextSiteId] = useState(siteId || "");

  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [prItemsByLineItem, setPRItemsByLineItem] = useState({});
  const [budgetDetails, setBudgetDetails] = useState({});

  const [formDetails, setFormDetails] = useState({
    prDate: new Date().toISOString().split("T")[0],
  });
  const [headerRequiredByDate, setHeaderRequiredByDate] = useState("");

  const [storeList, setStoreList] = useState([]);
  // Non-INACTIVE counterpart of storeList, used only to populate the actual "which store(s)
  // should this HO-to-Stores PR target" selection dropdowns (po-pr-short skill item 5) --
  // never for HO-site-name resolution or the Excel-upload mapper below, both of which
  // deliberately still need the full, unfiltered storeList (a HO location's own status is
  // unrelated to whether it should appear as a *target store* option, and Excel-upload
  // matching against a store code isn't a dropdown this item is about).
  const [activeStoreList, setActiveStoreList] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [procurementDestination, setProcurementDestination] = useState("SELF");

  // Concrete store(s) each HO multi-store item targets -- already the resolved list for
  // every destination type (selectedStores is set directly for SPECIFIC_STORES, and via
  // getResolvedStores for CITY/STATE/REGION below). Empty for SELF (single site: the HO
  // itself) and for SINGLE_STORE, neither of which need a per-store quantity split.
  const targetStores = isHeadOffice && procurementDestination !== "SELF" ? selectedStores : [];

  // Store Master (subway-intranet-api's central-service /stores) is only used to backfill
  // city/state/region on Location Master records that are missing it (matched by store
  // code). SELF/SPECIFIC_STORES stay on storeList (the Location master) untouched.
  const [storeMasterList, setStoreMasterList] = useState([]);

  // Resolved-stores viewer (read-only) for the City/State/Region chips — purely a display
  // aid, never read by buildPayload/validate/mapPrFileUploadResponse.
  const [viewerAnchor, setViewerAnchor] = useState(null);
  const [viewerContext, setViewerContext] = useState(null);

  // Location Master is the base/source-of-truth list (it's what the backend's own
  // resolveStores() queries against); Store Master only fills in a missing city/state/
  // region via store-code match. A store still missing any of the three after that is
  // dropped entirely — it must never appear in an option list or a resolved-store preview,
  // and resolution must never silently fall back to the full unfiltered store list.
  // Built from activeStoreList (ACTIVE-status only), not storeList -- see its declaration.
  const resolvedHOStores = useMemo(
    () => buildLocationFirstStores(activeStoreList, storeMasterList),
    [activeStoreList, storeMasterList]
  );
  const cityOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.city).filter(Boolean))].sort(),
    [resolvedHOStores]
  );
  const stateOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.state).filter(Boolean))].sort(),
    [resolvedHOStores]
  );
  const regionOptions = useMemo(
    () => [...new Set(resolvedHOStores.map((s) => s.region).filter(Boolean))].sort(),
    [resolvedHOStores]
  );

  const getResolvedStores = (field, values) => {
    const valueSet = new Set(values.map(normalizeCode));
    return resolvedHOStores.filter((s) => valueSet.has(normalizeCode(s[field])));
  };

  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [lineItemBudgetStats, setLineItemBudgetStats] = useState({});

  const [departmentList, setDepartmentList] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [prConfig, setPRConfig] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prUploadErrors, setPRUploadErrors] = useState([]);
  // SINGLE_STORE Excel upload, per line item: { nonce, updates } handed to
  // SingleStorePRSection, which applies it to its own rows (see single-store-upload.js).
  const [ssBulkUploads, setSSBulkUploads] = useState({});

  // For HEAD_OFFICE PRs, resolve the real HO site's own name/code from storeList
  // (already loaded for HO PRs, and unfiltered so it includes the HO location itself)
  // instead of budgetDetails, which only ever holds a pooled STORE's identity.
  const hoSite = isHeadOffice
    ? storeList.find((s) => String(s._id ?? s.id) === String(hoContextSiteId))
    : null;
  const { siteName = "", siteCode = "" } = isHeadOffice
    ? { siteName: hoSite?.name || "", siteCode: hoSite?.code || "" }
    : budgetDetails;

  useEffect(() => {
    loadDepartments();
    loadVendors();
    // Store Code resolution for the HO Excel-upload mapper needs the full store list
    // regardless of which "Procurement destination" (if any) has been picked yet.
    if (isHeadOffice) {
      loadStores();
      loadActiveStores();
      loadStoreMasterGeoOptions();
    }
    loadBudgetDetails(paramBudgetId, lineItemIdsFromUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedLineItems.length === 0) return;

    selectedLineItems.forEach((lineItem) => {
      const catId = Number(lineItem.category?.id);
      if (!catId) {
        return;
      }
      if (catId) loadSubcategoriesForLineItem(lineItem.id, catId);
      loadBudgetStatsForLineItem(lineItem);
    });

  }, [selectedLineItems, storeList]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── data loaders ─────────────────────────────────────────────────────────────

  const loadBudgetDetails = (bId, lineItemIds) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGETS}`,
      null,
      { page: 1, limit: 10, _id: bId, populateLocation: true }
    )
      .then((response) => {
        if (!response.message && response.results?.length > 0) {
          const budget = response.results[0];
          const site = budget.site || {};

          // prScope is authoritative from the URL: it's derived by the create-PR
          // modal directly from the actually-selected site (HO vs STORE). The
          // budget fetched here can be a pooled STORE budget even for a
          // HEAD_OFFICE-scoped PR (no HO-level budget document exists), so
          // budget.site.locationType must not override an explicit prScope —
          // doing so previously downgraded every HEAD_OFFICE PR to SINGLE_STORE.
          if (!searchParams.get("prScope")) {
            const locationType = site.locationType;
            if (!locationType) {
              toast.error(
                "Cannot determine PR scope: budget site type is unknown. Please contact support."
              );
              navigate(-1);
              return;
            }
            setPRScope(locationType === "STORE" ? "SINGLE_STORE" : "HEAD_OFFICE");
          }

          setBudgetDetails({
            ...budget,
            siteName: site.name || budget.siteName || "",
            siteCode: site.code || budget.siteCode || "",
            site: site._id || site.id || budget.site,
          });
          const filtered = (budget.lineItems || []).filter((l) =>
            lineItemIds.length > 0 ? lineItemIds.includes(l.id) : true
          );
          // Store Role is permanently hardcoded to the Procurement Budget Opex category --
          // no other budget category is ever shown or resolved for a Store user, regardless
          // of what else the resolved budget carries. Regular-user behavior is untouched.
          setSelectedLineItems(
            isStoreRole() ? filterStoreRoleLineItems(filtered) : filtered
          );
          if (budget.department && !userDepartmentId && !isStoreRole()) {
            setFormDetails((prev) => ({
              ...prev,
              department: { _id: budget.department, name: budget.departmentName },
            }));
            loadPRConfig(budget.department);
          }
        }
      })
      .catch(console.log);
  };

  const loadStores = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_LOCATION_LIST}`,
      null,
      { additionalFields: "locationType,city,region,state" }
    )
      .then((res) => {
        if (!res.message) setStoreList(Array.isArray(res) ? res : res?.results || []);
      })
      .catch(console.log);
  };

  // Non-INACTIVE counterpart of loadStores -- see activeStoreList's declaration. Filtered
  // here as status != INACTIVE rather than via the backend's activeOnly (status == ACTIVE),
  // since Location.status defaults to null and those stores must still be selectable.
  const loadActiveStores = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_LOCATION_LIST}`,
      null,
      { additionalFields: "locationType,city,region,state,status" }
    )
      .then((res) => {
        if (res.message) return;
        const list = Array.isArray(res) ? res : res?.results || [];
        setActiveStoreList(
          list.filter((s) => String(s?.status ?? "").trim().toUpperCase() !== "INACTIVE")
        );
      })
      .catch(console.log);
  };

  // Read-only fetch of the Store Master — used only to backfill city/state/region on
  // Location Master records that are missing it (see resolvedHOStores/buildLocationFirstStores).
  const loadStoreMasterGeoOptions = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.GET_ALL_STORES}`,
      null,
      { page: 1, limit: 5000 }
    )
      .then((res) => {
        if (res.message) return;
        setStoreMasterList(res.results || []);
      })
      .catch(console.log);
  };

  const loadSubcategoriesForLineItem = (lineItemId, categoryId) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.SUBCATEGORY}`,
      null,
      { page: 1, limit: 1000, category: categoryId }
    )
      .then((res) => {
        if (!res.message) {
          setSubcategoryMap((prev) => ({
            ...prev,
            [lineItemId]: res.results || [],
          }));
        }
      })
      .catch(console.log);
  };

  // HEAD_OFFICE PRs: this line item already belongs to budgetDetails, the single
  // HO-scoped budget resolved from the launcher modal's selected HO site
  // (hoContextSiteId) — look it up directly, the same way the SINGLE_STORE
  // branch below does, instead of re-pooling across every STORE location.
  const loadHeadOfficeBudgetStatsForLineItem = (lineItem) => {
    const lineItemId = lineItem._id ?? lineItem.id;
    const budgetId = budgetDetails._id ?? budgetDetails.id;
    if (!lineItemId || !budgetId) return;

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
      null,
      { lineItemIds: lineItemId, budgetIds: budgetId }
    )
      .then((res) => {
        if (!res.message) {
          setLineItemBudgetStats((prev) => ({ ...prev, [lineItem.id]: res }));
        }
      })
      .catch(console.log);
  };

  const loadBudgetStatsForLineItem = (lineItem) => {
    const budgetHead = lineItem._id || lineItem.id;
    const financialYear = budgetDetails.financialYear;
    if (!financialYear || !budgetHead) return;

    if (isHeadOffice) {
      loadHeadOfficeBudgetStatsForLineItem(lineItem);
      return;
    }

    const storeId = budgetDetails.site;
    if (!storeId) return;

    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_BUDGET_AVAILABILITY}`,
      null,
      { budgetHead, storeId, financialYear }
    )
      .then((res) => {
        if (!res.message) {
          setLineItemBudgetStats((prev) => ({ ...prev, [lineItem.id]: res }));
        }
      })
      .catch(console.log);
  };

  const loadDepartments = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`,
      null,
      { page: 1, limit: 1000 }
    )
      .then((res) => {
        if (!res.message) {
          const list = Array.isArray(res) ? res : res?.results || [];
          setDepartmentList(list);

          if (userDepartmentId && !isStoreRole()) {
            const matchedDept = list.find(
              (d) => (d._id || d.id) === userDepartmentId
            );
            if (matchedDept) {
              setFormDetails((prev) => ({ ...prev, department: matchedDept }));
              loadPRConfig(matchedDept._id || matchedDept.id);
            }
          }
        }
      })
      .catch(console.log);
  };

  const loadVendors = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INVOICE_TRACKING_HOSTNAME}${FINANCE_REST_URLS.VENDOR_LIST}`,
      null,
      { page: 1, limit: 1000, additionalFields: "paymentTerms,gst,address" }
    )
      .then((response) => {
        if (!response.message) {
          const list = response || [];
          setVendors(list);
          setFormDetails((prev) => {
            if (prev.preferredVendorCode && !prev.preferredVendor) {
              const found = list.find((v) => v.code === prev.preferredVendorCode);
              if (found) {
                return {
                  ...prev,
                  preferredVendor: found,
                  paymentTerms: prev.paymentTerms || found.paymentTerms || "",
                };
              }
            }
            return prev;
          });
        }
      })
      .catch(console.log);
  };

  const loadPRConfig = (departmentId) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_CONFIG}`,
      null,
      { department: departmentId }
    )
      .then((res) => {
        if (!res.message) setPRConfig(res || {});
      })
      .catch(console.log);
  };

  // ── item row handlers ─────────────────────────────────────────────────────────

  const onRowAdd = (lineItem) => {
    const items = prItemsByLineItem[lineItem.id] || [];
    setPRItemsByLineItem({
      ...prItemsByLineItem,
      [lineItem.id]: [
        ...items,
        {
          ...PR_ITEM_MODEL,
          budgetHead: lineItem.name || "",
          requiredByDate: headerRequiredByDate,
          _categoryId: Number(lineItem.category?.id),
          _tempId: `new-${Date.now()}`,
        },
      ],
    });
  };

  const updateItem = (lineItemId, index, updatedItem) => {
    const items = [...(prItemsByLineItem[lineItemId] || [])];
    items[index] = updatedItem;
    setPRItemsByLineItem({ ...prItemsByLineItem, [lineItemId]: items });
  };

  const deleteItem = (lineItemId, index) => {
    const items = [...(prItemsByLineItem[lineItemId] || [])];
    items.splice(index, 1);
    setPRItemsByLineItem({ ...prItemsByLineItem, [lineItemId]: items });
  };

  // Excel upload: merges resolved rows into their matching budget-head groups alongside
  // whatever's already there (manual or previously uploaded) — upload and manual entry are
  // not mutually exclusive here, unlike the PO upload flow. Invalid rows are never merged in;
  // they're only surfaced in prUploadErrors.
  const onPrItemsUpload = async (response) => {
    const { data } = response;
    const { grouped, errors, derivedSelection } = await mapPrFileUploadResponse(data, {
      selectedLineItems,
      storeList,
      storeMasterList,
      procurementDestination,
      selectedStores,
      prType,
      financialYear: budgetDetails.financialYear,
      hoSiteId: siteIdFromUrl || budgetDetails.site,
      prDate: formDetails.prDate,
      isHeadOffice,
    });

    if (derivedSelection) {
      if (procurementDestination === "SPECIFIC_STORES") {
        setSelectedStores(derivedSelection.stores || []);
      } else if (procurementDestination === "CITY") {
        setSelectedCities(derivedSelection.cities || []);
        setSelectedStores(getResolvedStores("city", derivedSelection.cities || []));
      } else if (procurementDestination === "STATE") {
        setSelectedStates(derivedSelection.states || []);
        setSelectedStores(getResolvedStores("state", derivedSelection.states || []));
      } else if (procurementDestination === "REGION") {
        setSelectedRegions(derivedSelection.regions || []);
        setSelectedStores(getResolvedStores("region", derivedSelection.regions || []));
      }
    }
    if (Object.keys(grouped).length > 0) {
      setPRItemsByLineItem((prev) => {
        const next = { ...prev };
        Object.entries(grouped).forEach(([lineItemId, items]) => {
          next[lineItemId] = [...(next[lineItemId] || []), ...items];
        });
        return next;
      });
    }
    setPRUploadErrors(errors);
    if (errors.length > 0) {
      toast.warning(
        `${errors.length} row(s) could not be added — see details below the upload button.`,
        { autoClose: 4000 }
      );
    }
  };

  // SINGLE_STORE: the template is the grid's own current rows (live catalog + vendor
  // pricing), so it can only be built once every section has loaded its items.
  const onDownloadSingleStoreTemplate = () => {
    const written = downloadSingleStorePRTemplate({
      selectedLineItems,
      prItemsByLineItem,
      subcategoryMap,
      siteCode,
    });
    if (!written) toast.error("Items are still loading — please try again in a moment");
  };

  // SINGLE_STORE Excel upload: quantities from the file are applied onto the rows already in
  // the grid (matched by hidden item reference); rate/tax/amounts always come from the grid.
  // Store context is the page's own budgetDetails.site, same as the manual flow.
  const onSingleStoreItemsUpload = (response) => {
    const { data } = response;
    const { updatesByLineItem, errors, includedCount } = mapSingleStorePrUploadResponse(data, {
      selectedLineItems,
      prItemsByLineItem,
    });
    const nonce = Date.now();
    setSSBulkUploads(
      Object.fromEntries(
        Object.entries(updatesByLineItem).map(([lineItemId, updates]) => [
          lineItemId,
          { nonce, updates },
        ])
      )
    );
    setPRUploadErrors(errors);
    if (Object.keys(updatesByLineItem).length > 0) {
      toast.success(`${includedCount} item(s) with Qty > 0 added from the upload`, { autoClose: 3000 });
    }
    if (errors.length > 0) {
      toast.warning(
        `${errors.length} row(s) could not be added — see details below the upload button.`,
        { autoClose: 4000 }
      );
    }
  };

  // ── submit ────────────────────────────────────────────────────────────────────

  const buildPayload = () => {
    const allItems = [];
    Object.entries(prItemsByLineItem).forEach(([lineItemId, items]) => {
      items.forEach((item) => {
        const pooledQuantity = getPooledQuantity(item);
        if (!isHeadOffice && pooledQuantity === 0) return;

        // Only allocationId/storeId/requestedQuantity ever go out -- reservedQuantity/
        // convertedQuantity/shortClosedQuantity are backend-owned (allocation.service.js)
        // and the backend's Joi schema forbids a client from sending them, so they're
        // stripped here even if `item.allocations` (e.g. loaded from an existing PR via
        // loadExistingPR) still carries them from the server's own persisted document.
        const allocations = (item.allocations || [])
          .filter((a) => a.requestedQuantity !== undefined && a.requestedQuantity !== null)
          .map((a) => ({
            allocationId: a.allocationId || undefined,
            storeId: a.storeId,
            requestedQuantity: parseFloat(a.requestedQuantity),
          }));
        const hasAllocations = allocations.length > 0;

        allItems.push({
          _id: item._id || undefined,
          item: item.item,
          // category/subCategory/categoryMappingId are resolved once, up front, against the
          // item's actual categoryMappings -- by single-store-row.js's auto-populate-items
          // call, add-item-modal.js's Subcategory+Item selection, or mapPrFileUploadResponse's
          // resolveMatchingCategoryMapping -- so they're always the specific pair relevant to
          // this line item's category, even for an item with more than one pair. Sending
          // categoryMappingId alongside the category/subCategory pair gives the backend the
          // most unambiguous selector available.
          category: item.category ?? undefined,
          subCategory: item.subCategory ?? undefined,
          categoryMappingId: item.categoryMappingId || undefined,
          lineItem: lineItemId === "misc" ? undefined : lineItemId,
          budgetHead: item.budgetHead || "",
          description: item.description || "",
          hsnCode: item.hsnCode || "",
          uom: item.uom || "",
          // Quantity is only sent when there's no per-store allocation intent -- the
          // backend always re-derives it from the merged allocations' sum when they're
          // present, so sending both would just be a stale/ignored value.
          ...(hasAllocations ? {} : { quantity: pooledQuantity }),
          estimatedRate: parseFloat(item.estimatedRate) || 0,
          estimatedAmount: item.estimatedAmount || 0,
          taxRate: parseFloat(item.taxRate) || 0,
          estimatedTaxAmount: item.estimatedTaxAmount || 0,
          estimatedTotalAmount: item.estimatedTotalAmount || 0,
          remarks: item.remarks || "",
          rateContract: item.rateContract || false,
          vendorCode: item.vendorCode || "",
          vendorName: item.vendorName || "",
          requiredByDate: item.requiredByDate || headerRequiredByDate || null,
          ...(hasAllocations ? { allocations } : {}),
        });
      });
    });

    // `site` for a HEAD_OFFICE PR is never derived here from siteId/budgetDetails.site (a
    // pooled STORE's budget) -- the backend now resolves it exclusively from
    // hoContextSiteId, seeded from the launcher modal's siteId.
    const basePayload = {
      prDate: formDetails.prDate,
      items: allItems,
      preferredVendorCode: formDetails.preferredVendorCode || "",
      preferredVendorName: formDetails.preferredVendorName || "",
      vendorRemarks: formDetails.vendorRemarks || "",
      justification: formDetails.justification || "",
      documentLinks: attachments,
    };

    return {
      ...basePayload,
      prScope,
      prType,
      budget: activeBudgetId,
      department: formDetails.department?._id || formDetails.department?.id,
      paymentTerms: formDetails.paymentTerms || "",
      ...(isHeadOffice
        ? { hoContextSiteId: Number(hoContextSiteId) }
        : { site: budgetDetails.site }),
      ...(isHeadOffice && {
        storeSelectionType: procurementDestination,
        selectedSiteIds: procurementDestination !== "SELF"
          ? selectedStores.map((s) => s._id || s.id)
          : [],
        selectedCities: procurementDestination === "CITY" ? selectedCities : [],
        selectedStates: procurementDestination === "STATE" ? selectedStates : [],
        selectedRegions: procurementDestination === "REGION" ? selectedRegions : [],
      }),
    };
  };

  const validate = (shouldSubmit) => {
    const allItems = Object.values(prItemsByLineItem).flat();
    const submitItems = isHeadOffice
      ? allItems
      : allItems.filter((i) => (parseFloat(i.quantity) || 0) > 0);

    if (submitItems.length === 0) {
      toast.error("Please add at least one item with quantity > 0");
      return false;
    }
    if (shouldSubmit && isHeadOffice && allItems.some((i) => getPooledQuantity(i) <= 0)) {
      toast.error("Every item must have a quantity greater than 0 to submit this PR");
      return false;
    }
    if (isHeadOffice && procurementDestination === "SPECIFIC_STORES" && selectedStores.length === 0) {
      toast.error("Please select at least one target store");
      return false;
    }
    if (isHeadOffice && procurementDestination === "CITY" && selectedCities.length === 0) {
      toast.error("Please select at least one target city");
      return false;
    }
    if (isHeadOffice && procurementDestination === "STATE") {
      if (selectedStates.length === 0) {
        toast.error("Please select a target state");
        return false;
      }
      if (selectedStates.length > 1) {
        toast.error("Only one target state can be selected");
        return false;
      }
    }
    if (isHeadOffice && procurementDestination === "REGION") {
      if (selectedRegions.length === 0) {
        toast.error("Please select a target region");
        return false;
      }
      if (selectedRegions.length > 1) {
        toast.error("Only one target region can be selected");
        return false;
      }
    }
    if (prConfig.requireJustification && !formDetails.justification?.trim()) {
      toast.error("Justification is required for this department");
      return false;
    }
    if (prConfig.requireAttachment && attachments.length === 0) {
      toast.error("Supporting document is required for this department");
      return false;
    }
    return true;
  };

  const submitPR = (prId) => {
    invokeApi(HTTP_METHODS.POST, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT}${Number(prId)}`)
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("PR submitted for approval successfully");
          navigate(`/finance-v2/purchase-requisition/${prId}`);
        }
      })
      .catch(() => toast.error("Failed to submit PR"))
      .finally(() => setIsSubmitting(false));
  };

  const submitParentPR = (parentId) => {
    invokeApi(HTTP_METHODS.POST, `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PR_SUBMIT_CHILDREN}${Number(parentId)}`)
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
          return;
        }
        const { succeededCount = 0, failedCount = 0, total = 0 } = res.summary || {};
        if (failedCount === 0) {
          toast.success(
            total > 1
              ? `PR submitted for approval successfully across ${succeededCount} state(s)`
              : "PR submitted for approval successfully"
          );
        } else if (succeededCount > 0) {
          toast.error(`${succeededCount} of ${total} state PRs submitted; ${failedCount} failed -- open the PR to retry the rest`);
        } else {
          toast.error("Failed to submit PR for any state -- open the PR to retry");
        }
        navigate(`/finance-v2/purchase-requisition/${parentId}`);
      })
      .catch(() => toast.error("Failed to submit PR"))
      .finally(() => setIsSubmitting(false));
  };

  const savePR = (shouldSubmit) => {
    if (!validate(shouldSubmit)) return;
    setIsSubmitting(true);
    const payload = buildPayload();

    invokeApi(
      HTTP_METHODS.POST,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}`,
      payload
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
          setIsSubmitting(false);
          return;
        }
        toast.success("PR created successfully");
        if (shouldSubmit) {
          if (res.isParentPR) {
            submitParentPR(res.id || res._id);
          } else {
            submitPR(res.id || res._id);
          }
        } else {
          navigate(`/finance-v2/purchase-requisition/${res.id || res._id}`);
          setIsSubmitting(false);
        }
      })
      .catch(() => {
        toast.error("Failed to save PR");
        setIsSubmitting(false);
      });
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="finance-v2-create-po-container finance-v2-create-pr-page">
      {/* Page header */}
      <div className="po-create-header">
        <div className="left-container">
          <div className="back-title">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
            <Typography className="title">
              Create Purchase Requisition
            </Typography>
          </div>
          <div className="info-section">
            <div className="info-box">
              <span>Site Name</span>
              <span>{siteName || "-"}</span>
            </div>
            <div className="info-box">
              <span>Site Code</span>
              <span>{siteCode || "-"}</span>
            </div>
            <div className="info-box">
              <span>PR Type</span>
              <span>{prType || "-"}</span>
            </div>
            <div className="info-box">
              <span>PR Amount</span>
              <span>₹{getPRTotalAmount(prItemsByLineItem)}</span>
            </div>
          </div>
        </div>
        <div className="right-container">
          <Button variant="outlined" disabled={isSubmitting} onClick={() => savePR(false)}>
            Save as Draft
          </Button>
          <Button variant="outlined" disabled={isSubmitting} onClick={() => savePR(true)}>
            Save &amp; Submit
          </Button>
        </div>
      </div>

      {/* Form fields */}
      <div className="form-container">
        {/* Row 1: Department | PR Scope | Requested By | Financial Year | Budget Type */}
        <div className="form-row">
          {isStoreRole() ? (
            <TextField size="small" label="Department" value="Store" disabled />
          ) : (
            <Autocomplete
              value={formDetails.department || null}
              disabled
              onChange={(e, val) => {
                setFormDetails((prev) => ({ ...prev, department: val }));
                if (val?._id || val?.id) loadPRConfig(val._id || val.id);
              }}
              options={departmentList}
              getOptionLabel={(op) => op.name || ""}
              isOptionEqualToValue={(opt, val) => (opt._id || opt.id) === (val._id || val.id)}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Department" />
              )}
            />
          )}
          <TextField
            value={isHeadOffice ? "Head Office" : "Single Store"}
            disabled
            size="small"
            label="PR Scope"
            fullWidth
          />
          <TextField
            value={userDetails.name || ""}
            disabled
            size="small"
            label="Requested By"
            fullWidth
          />
          <TextField
            value={getFinancialYears().defaultFY}
            disabled
            size="small"
            label="Financial Year"
            fullWidth
          />
          <TextField
            value={getBudgetTypeLabel(budgetDetails.budgetType)}
            disabled
            size="small"
            label="Budget Type"
            fullWidth
          />
        </div>

        {/* Row 2: PR Date | Required By Date | Remarks | Justification | Payment Terms */}
        <div className="form-row">
          <TextField
            label="PR Date"
            type="date"
            value={formDetails.prDate || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, prDate: e.target.value }))
            }
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Required By Date"
            type="date"
            value={headerRequiredByDate}
            onChange={(e) => setHeaderRequiredByDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Remarks"
            value={formDetails.remarks || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, remarks: e.target.value }))
            }
            size="small"
            fullWidth
          />
          <TextField
            label={`Justification${prConfig.requireJustification ? " *" : ""}`}
            value={formDetails.justification || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, justification: e.target.value }))
            }
            size="small"
            fullWidth
          />
          <TextField
            value={formDetails.paymentTerms || ""}
            onChange={(e) =>
              setFormDetails((prev) => ({ ...prev, paymentTerms: e.target.value }))
            }
            size="small"
            label="Payment Terms"
            fullWidth
          />
        </div>

        {/* HEAD_OFFICE: procurement destination + conditional target picker */}
        {isHeadOffice && (
          <div style={{ marginTop: 16 }}>
            <FormControl>
              <FormLabel>Procurement destination</FormLabel>
              <RadioGroup
                row
                value={procurementDestination}
                onChange={(e) => {
                  const dest = e.target.value;
                  setProcurementDestination(dest);
                  setSelectedStores([]);
                  setSelectedCities([]);
                  setSelectedStates([]);
                  setSelectedRegions([]);
                  if (dest !== "SELF" && storeList.length === 0) {
                    loadStores();
                  }
                  if (dest !== "SELF" && activeStoreList.length === 0) {
                    loadActiveStores();
                  }
                  if (dest !== "SELF" && cityOptions.length === 0) {
                    loadStoreMasterGeoOptions();
                  }
                }}
              >
                <FormControlLabel value="SELF" control={<Radio />} label="Head Office" />
                <FormControlLabel value="SPECIFIC_STORES" control={<Radio />} label="Stores" />
                <FormControlLabel value="CITY" control={<Radio />} label="Cities" />
                <FormControlLabel value="STATE" control={<Radio />} label="States" />
                <FormControlLabel value="REGION" control={<Radio />} label="Regions" />
              </RadioGroup>
            </FormControl>
            {procurementDestination === "SPECIFIC_STORES" && (
              <Autocomplete
                multiple
                value={selectedStores}
                onChange={(e, val) => setSelectedStores(val || [])}
                options={activeStoreList.filter(
                  (s) =>
                    s.locationType === "STORE" &&
                    String(s._id || s.id) !== String(hoContextSiteId)
                )}
                getOptionLabel={(op) => (op.code ? `${op.code} - ${op.name}` : op.name || "")}
                isOptionEqualToValue={(opt, val) =>
                  (opt._id || opt.id) === (val._id || val.id)
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Target Stores *" />
                )}
                style={{ marginTop: 12 }}
              />
            )}
            {procurementDestination === "CITY" && (
              <>
                <Autocomplete
                  multiple
                  value={selectedCities}
                  onChange={(e, val) => {
                    const cities = val || [];
                    setSelectedCities(cities);
                    setSelectedStores(getResolvedStores("city", cities));
                  }}
                  options={cityOptions}
                  renderTags={(value, getTagProps) =>
                    value.map((val, index) => {
                      const matches = getResolvedStores("city", [val]);
                      return (
                        <Chip
                          icon={<Visibility fontSize="small" />}
                          label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                          size="small"
                          {...getTagProps({ index })}
                          onClick={(e) => {
                            setViewerAnchor(e.currentTarget);
                            setViewerContext({ title: `Stores in ${val}`, stores: matches });
                          }}
                        />
                      );
                    })
                  }
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target Cities *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedCities.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click a selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            {procurementDestination === "STATE" && (
              <>
                <Autocomplete
                  value={selectedStates[0] || null}
                  onChange={(e, val) => {
                    // Single-select: STATE targeting must resolve to exactly one state group.
                    const states = val ? [val] : [];
                    setSelectedStates(states);
                    setSelectedStores(getResolvedStores("state", states));
                  }}
                  options={stateOptions}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target State *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedStates.map((val) => {
                  const matches = getResolvedStores("state", [val]);
                  return (
                    <Chip
                      key={val}
                      icon={<Visibility fontSize="small" />}
                      label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={(e) => {
                        setViewerAnchor(e.currentTarget);
                        setViewerContext({ title: `Stores in ${val}`, stores: matches });
                      }}
                    />
                  );
                })}
                {selectedStates.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click the selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            {procurementDestination === "REGION" && (
              <>
                <Autocomplete
                  value={selectedRegions[0] || null}
                  onChange={(e, val) => {
                    // Single-select: REGION targeting must resolve to exactly one region group.
                    const regions = val ? [val] : [];
                    setSelectedRegions(regions);
                    setSelectedStores(getResolvedStores("region", regions));
                  }}
                  options={regionOptions}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Target Region *" />
                  )}
                  style={{ marginTop: 12 }}
                />
                {selectedRegions.map((val) => {
                  const matches = getResolvedStores("region", [val]);
                  return (
                    <Chip
                      key={val}
                      icon={<Visibility fontSize="small" />}
                      label={`${val} · ${matches.length} store${matches.length === 1 ? "" : "s"}`}
                      size="small"
                      style={{ marginTop: 8 }}
                      onClick={(e) => {
                        setViewerAnchor(e.currentTarget);
                        setViewerContext({ title: `Stores in ${val}`, stores: matches });
                      }}
                    />
                  );
                })}
                {selectedRegions.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    Click the selected value to view resolved stores.
                  </Typography>
                )}
              </>
            )}
            <ResolvedStoresViewer
              anchorEl={viewerAnchor}
              open={Boolean(viewerAnchor)}
              onClose={() => setViewerAnchor(null)}
              title={viewerContext?.title}
              stores={viewerContext?.stores || []}
            />
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="po-additional-attachment-container">
        <FileUpload
          uploadText={`Supporting Documents${prConfig.requireAttachment ? " *" : ""}`}
          clsName="add-additional-doc"
          id="pr-attachments"
          url={`${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.UPLOAD_DOCUMENT}`}
          callBack={(response) => {
            const url = response?.data?.docUrl || response?.data?.url;
            if (url) setAttachments((prev) => [...prev, url]);
          }}
          formDataKey="file"
        />
        {attachments.map((url, idx) => (
          <Chip
            key={idx}
            label={`Doc ${idx + 1}`}
            onDelete={() => setAttachments(attachments.filter((_, i) => i !== idx))}
            onClick={() => window.open(url, "_blank")}
          />
        ))}
      </div>

      {/* Excel upload — HEAD_OFFICE only */}
      {isHeadOffice && (
        <div className="po-attachment-form-container">
          <FileUpload
            uploadText="Upload PR Item Details"
            clsName="add-additional-doc"
            id="pr-items-excel"
            url={`${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.UPLOAD_MASTER_DATA}pr-item-details?site=${
              hoContextSiteId || ""
            }&lineItemIds=${selectedLineItems.map((li) => li.id).join(",")}`}
            callBack={onPrItemsUpload}
            formDataKey="file"
          />
          <DownloadTemplateButton
            type={
              procurementDestination === "SELF"
                ? uploadTemplates["pr-items-self"]
                : uploadTemplates["pr-items"]
            }
          />
        </div>
      )}
      {/* Excel upload — SINGLE_STORE (pre-filled catalog template, Qty/Remarks only) */}
      {!isHeadOffice && selectedLineItems.length > 0 && (
        <div className="po-attachment-form-container">
          <FileUpload
            uploadText="Upload PR Item Quantities"
            clsName="add-additional-doc"
            id="pr-ss-items-excel"
            url={`${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.UPLOAD_MASTER_DATA}pr-item-details?prScope=SINGLE_STORE&site=${
              budgetDetails.site || ""
            }&lineItemIds=${selectedLineItems.map((li) => li.id).join(",")}`}
            callBack={onSingleStoreItemsUpload}
            formDataKey="file"
          />
          <Button variant="outlined" onClick={onDownloadSingleStoreTemplate}>
            Download Template
          </Button>
        </div>
      )}
      {prUploadErrors.length > 0 && (
        <div className="po-attachment-form-container finance-v2-pr-upload-errors">
          <div className="pr-upload-errors-header">
            <Typography color="error" variant="subtitle2">
              {prUploadErrors.length} row(s) skipped due to validation errors
            </Typography>
            <IconButton size="small" onClick={() => setPRUploadErrors([])}>
              <Close fontSize="small" />
            </IconButton>
          </div>
          {prUploadErrors.map((err, i) => (
            <Typography key={i} color="error" variant="body2">
              Row {err.row ?? "-"}: {err.message}
            </Typography>
          ))}
        </div>
      )}

      {/* Items per budget line item */}
      <div className="finance-v2-po-add-items-container">
        <div className="added-po-items-list">
          {selectedLineItems.length === 0 && (
            <div className="po-attachment-form-container">
              <Typography color="text.secondary">
                {isStoreRole()
                  ? `No approved "${STORE_ROLE_BUDGET_CATEGORY_NAME}" budget category found for your store. Please contact support.`
                  : "No budget categories selected. Please go back and select categories."}
              </Typography>
            </div>
          )}

          {selectedLineItems.map((lineItem) => {
            const items = prItemsByLineItem[lineItem.id] || [];
            const lineItemTotal = parseFloat(getLineItemTotal(items));
            const available = lineItem.availableAmount || 0;
            const isOver = lineItemTotal > available;
            const stats = lineItemBudgetStats[lineItem.id];

            return (
              <Accordion key={lineItem.id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className="po-accordion-summary">
                  <div className="accordion-header">
                    <div className="left-container">
                      <Typography>
                        {lineItem.name} ({items.length})
                      </Typography>
                      {isHeadOffice && (
                        <IconButton
                          onClick={(e) => {
                            onRowAdd(lineItem);
                            e.stopPropagation();
                          }}
                        >
                          <AddCircle color="primary" />
                        </IconButton>
                      )}
                    </div>
                    <div className="right-container">
                      <BudgetAvailability
                        stats={stats}
                        available={available}
                        lineItemTotal={lineItemTotal}
                        isOver={isOver}
                      />
                    </div>
                  </div>
                </AccordionSummary>

                <AccordionDetails sx={{ overflowX: "auto", p: 1 }}>
                  {isHeadOffice ? (
                    <>
                      {items.length > 0 && renderHOColumnHeader()}
                      {items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                          Click + to add items
                        </Typography>
                      ) : (
                        items.map((prItem, index) => (
                          <PRItemRow
                            key={prItem._tempId || index}
                            prItem={prItem}
                            subcategoryList={subcategoryMap[lineItem.id] || []}
                            allVendors={vendors}
                            budgetHeadName={lineItem.name || ""}
                            headerRequiredByDate={headerRequiredByDate}
                            prDate={formDetails.prDate}
                            targetStores={targetStores}
                            onUpdate={(item) => updateItem(lineItem.id, index, item)}
                            onDelete={() => deleteItem(lineItem.id, index)}
                          />
                        ))
                      )}
                    </>
                  ) : (
                    <SingleStorePRSection
                      lineItem={lineItem}
                      existingItems={prItemsByLineItem[lineItem.id] || []}
                      subcategoryList={subcategoryMap[lineItem.id] || []}
                      prDate={formDetails.prDate}
                      bulkUpload={ssBulkUploads[lineItem.id]}
                      onItemsChange={(rows) =>
                        setPRItemsByLineItem((prev) => ({ ...prev, [lineItem.id]: rows }))
                      }
                    />
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CreatePR;
